import { inject } from '@angular/core'
import { performTsRestApiRequest } from '@ftr/api-shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { Loading, RemoteData } from '@ftr/foundation'
import { LaunchDarklyService, WebFeatureFlags } from '@ftr/ui-feature-flags'
import { map, Observable, of, switchMap } from 'rxjs'
import { AnnotationsApiClient, AnnotationsApiClientFactory } from '../../api-client'

export abstract class AnnotationsService {
  launchDarklyService = inject(LaunchDarklyService)

  constructor(
    private readonly apiClientFactory: AnnotationsApiClientFactory,
    private readonly regionCache: CourtSystemRegionCache,
  ) {}

  protected performApiRequest<T, U>({
    requestFn,
    responseMapFn,
    responseErrorMapFn = result => RemoteData.failure(result),
    courtSystemId,
  }: {
    courtSystemId: string
    requestFn: (client: AnnotationsApiClient) => Promise<T>
    responseMapFn: (result: T) => RemoteData<U>
    responseErrorMapFn?: (result: Error) => RemoteData<U, Error>
  }): Observable<Loading | RemoteData<U, Error>> {
    return performTsRestApiRequest(this.getApiClient(courtSystemId), { requestFn, responseMapFn, responseErrorMapFn })
  }

  protected getApiClient(courtSystemId: string): Observable<AnnotationsApiClient> {
    return this.regionCache
      .getOrFetchCourtSystem(of(courtSystemId))
      .pipe(map(({ region }) => this.apiClientFactory.build(region)))
  }

  /**
   * Runs an operation conditionally based on a feature flag.
   * If the flag is enabled, returns the provided value wrapped in an Observable.
   * Otherwise, runs the provided function
   * @param flag
   * @param value
   * @param func
   * @protected
   */
  protected runIfFeatureFlagEnabled<T>(
    flag: keyof WebFeatureFlags,
    value: Observable<RemoteData<T>>,
    func: Observable<Loading | RemoteData<T>>,
  ): Observable<Loading | RemoteData<T>> {
    const hasFlag = this.launchDarklyService.observeFlagChanges(flag)

    return hasFlag.pipe(
      switchMap(enabled => {
        if (!enabled) {
          return value
        }

        return func
      }),
    )
  }
}
